import { useShopI18n } from '../../i18n'
import type { CheckoutCartSnapshotItem, CheckoutPaymentProviderOption, CheckoutPaymentProviderId } from '../types'

type TotalsRow = {
  label: string
  value: string
  strong?: boolean
}

type Props = {
  items: CheckoutCartSnapshotItem[]
  formatCurrency: (value: number) => string
  shippingSummary: string
  billingSummary: string
  shippingMethodSummary: string
  paymentProviders: CheckoutPaymentProviderOption[]
  selectedPaymentProvider: CheckoutPaymentProviderId
  selectedPaymentProviderRequiresRedirect: boolean
  totalsRows: TotalsRow[]
  disabled: boolean
  onConfirm: () => void
  onEditShipping: () => void
  onEditBilling: () => void
  onEditShippingMethod: () => void
  onSelectPaymentProvider: (provider: CheckoutPaymentProviderId) => void
  onBack: () => void
}

export const ReviewStep = ({
  items,
  formatCurrency,
  shippingSummary,
  billingSummary,
  shippingMethodSummary,
  paymentProviders,
  selectedPaymentProvider,
  selectedPaymentProviderRequiresRedirect,
  totalsRows,
  disabled,
  onConfirm,
  onEditShipping,
  onEditBilling,
  onEditShippingMethod,
  onSelectPaymentProvider,
  onBack,
}: Props) => {
  const { t } = useShopI18n()

  return (
    <section className="space-y-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6">
      <header>
        <h2 className="text-xl font-semibold text-slate-900">{t('checkout.review.title')}</h2>
        <p className="mt-1 text-sm text-slate-600">{t('checkout.review.description')}</p>
      </header>

      <article className="rounded-xl border border-slate-200 p-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600">{t('checkout.review.products')}</h3>
        <ul className="mt-2 space-y-2">
          {items.map((item) => (
            <li key={item.itemId} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2">
              <div className="min-w-0">
                <p className="line-clamp-1 text-sm font-medium text-slate-900">{item.productName}</p>
                <p className="text-xs text-slate-600">x{item.quantity}</p>
              </div>
              <p className="text-sm font-semibold text-slate-900">{formatCurrency(item.lineTotal)}</p>
            </li>
          ))}
        </ul>
      </article>

      <div className="grid gap-3 md:grid-cols-3">
        <article className="rounded-xl border border-slate-200 p-3">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600">{t('checkout.review.shippingAddress')}</h3>
            <button type="button" className="text-xs font-semibold text-slate-700 underline underline-offset-2" onClick={onEditShipping}>
              {t('checkout.cta.edit')}
            </button>
          </div>
          <p className="mt-2 text-sm text-slate-900">{shippingSummary}</p>
        </article>
        <article className="rounded-xl border border-slate-200 p-3">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600">{t('checkout.review.billingAddress')}</h3>
            <button type="button" className="text-xs font-semibold text-slate-700 underline underline-offset-2" onClick={onEditBilling}>
              {t('checkout.cta.edit')}
            </button>
          </div>
          <p className="mt-2 text-sm text-slate-900">{billingSummary}</p>
        </article>
        <article className="rounded-xl border border-slate-200 p-3">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600">{t('checkout.review.shippingMethod')}</h3>
            <button type="button" className="text-xs font-semibold text-slate-700 underline underline-offset-2" onClick={onEditShippingMethod}>
              {t('checkout.cta.edit')}
            </button>
          </div>
          <p className="mt-2 text-sm text-slate-900">{shippingMethodSummary}</p>
        </article>
      </div>

      <article className="rounded-xl border border-slate-200 p-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600">{t('checkout.review.paymentMethod')}</h3>
        <div className="mt-3 grid gap-2">
          {paymentProviders.filter((provider) => provider.enabled).map((provider) => (
            <label
              key={provider.id}
              className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2 ${
                selectedPaymentProvider === provider.id
                  ? 'border-emerald-300 bg-emerald-50'
                  : 'border-slate-200 bg-white'
              }`}
            >
              <input
                type="radio"
                name="checkout-payment-provider"
                className="mt-0.5 h-4 w-4"
                checked={selectedPaymentProvider === provider.id}
                onChange={() => onSelectPaymentProvider(provider.id)}
              />
              <span>
                <span className="block text-sm font-semibold text-slate-900">{provider.label}</span>
                <span className="block text-xs text-slate-600">{provider.description}</span>
              </span>
            </label>
          ))}
        </div>
      </article>

      <article className="rounded-xl border border-slate-200 bg-slate-50 p-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600">{t('checkout.review.totals')}</h3>
        <div className="mt-2 space-y-1 text-sm">
          {totalsRows.map((row) => (
            <div key={row.label} className={`flex items-center justify-between ${row.strong ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>
              <span>{row.label}</span>
              <span>{row.value}</span>
            </div>
          ))}
        </div>
      </article>

      <div className="flex items-center justify-between">
        <button
          type="button"
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500"
          onClick={onBack}
        >
          {t('checkout.cta.back')}
        </button>
        <button
          type="button"
          className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={disabled}
          onClick={onConfirm}
        >
          {selectedPaymentProviderRequiresRedirect
            ? t('checkout.cta.proceedToPayment')
            : t('checkout.cta.confirmBankTransferOrder')}
        </button>
      </div>
    </section>
  )
}
